import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, BadgeCheck, Check } from 'lucide-react'
import { Wordmark } from '#/components/layout/wordmark'
import { Markdown } from '#/components/common/markdown'
import { FeatureIcon } from '#/components/roadmap/feature-icon'
import { api } from '#/lib/api'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

interface ShareEvidence { tasks: number; done: number; verified: number; override: number; failed: number }
interface ShareFeature { name: string; summary: string | null; phase: number; icon: string | null; evidence?: ShareEvidence }
interface ShareProof { tasks: number; done: number; verified: number; override: number; failed: number; passRate: number; lastVerifiedAt: string | null }
interface ShareApproval { phase: number; layer: string; approvedBy: string; createdAt: string }
interface ShareData { title: string; prd: string; features: ShareFeature[]; proof?: ShareProof; approvals?: ShareApproval[] }

const layerLabel = (layer: string) =>
  layer.startsWith('task:') ? m.pf_layer_task() : layer === 'frontend' ? m.pf_layer_frontend() : layer === 'backend' ? m.pf_layer_backend() : m.pf_layer_all()

/** Counts-only evidence bar: verified (green) · override (amber) · claimed but unproven (muted) · not started (empty). */
function EvidenceBar({ e }: { e: ShareEvidence }) {
  if (!e.tasks) return null
  const claimed = Math.max(0, e.done - e.verified - e.override)
  const pct = (n: number) => `${(n / e.tasks) * 100}%`
  return (
    <div className="mt-2">
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" title={m.pf_ring_title({ verified: e.verified, override: e.override, claimed, total: e.tasks })}>
        <span className="bg-status-done" style={{ width: pct(e.verified) }} />
        <span className="bg-status-progress" style={{ width: pct(e.override) }} />
        <span className="bg-status-todo/45" style={{ width: pct(claimed) }} />
      </div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="tabular-nums">{m.pf_feature_bar({ verified: e.verified, done: e.done })}</span>
        {e.override > 0 ? <span className="text-status-progress">{m.pf_node_override({ n: e.override })}</span> : null}
      </div>
    </div>
  )
}

function ProofCard({ proof, approvals }: { proof: ShareProof; approvals: ShareApproval[] }) {
  const pct = Math.round(proof.passRate * 100)
  return (
    <section className="rounded-xl border bg-card p-3.5">
      <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <BadgeCheck className="size-3.5 text-status-done" /> {m.pf_title()}
      </h2>
      <div className="mt-2.5 flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums text-status-done">{pct}%</span>
        <span className="text-xs text-muted-foreground">{m.pf_pass_rate()}</span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{m.pf_pass_rate_hint()}</p>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg border bg-background p-2">
          <dt className="text-[11px] text-muted-foreground">{m.pf_tasks_verified()}</dt>
          <dd className="tabular-nums font-medium text-status-done">{proof.verified}<span className="text-muted-foreground"> / {proof.tasks}</span></dd>
        </div>
        <div className="rounded-lg border bg-background p-2">
          <dt className="text-[11px] text-muted-foreground">{m.pf_overrides()}</dt>
          <dd className={proof.override > 0 ? 'tabular-nums font-medium text-status-progress' : 'tabular-nums font-medium'}>{proof.override}</dd>
        </div>
      </dl>
      <div className="mt-2 text-[11px] text-muted-foreground">
        {m.pf_last_verified()}: <span className="text-foreground">{proof.lastVerifiedAt ? fmtDate(proof.lastVerifiedAt) : m.pf_never()}</span>
      </div>
      <div className="mt-3 border-t pt-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{m.pf_approvals_title()}</div>
        {approvals.length ? (
          <ul className="mt-1.5 space-y-1 text-xs">
            {approvals.map((a) => (
              <li key={`${a.phase}-${a.layer}-${a.createdAt}`} className="flex items-center gap-1.5">
                <Check className="size-3.5 shrink-0 text-status-done" />
                <span>{m.pf_approval_item({ n: a.phase, layer: layerLabel(a.layer) })}</span>
                <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{fmtDate(a.createdAt, { day: 'numeric', month: 'short' })}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-[11px] text-muted-foreground">{m.pf_approvals_empty()}</p>
        )}
      </div>
    </section>
  )
}

export const Route = createFileRoute('/p/$slug')({ component: Share })

function Share() {
  const { slug } = Route.useParams()
  const { data, error } = useQuery({ queryKey: ['share', slug], queryFn: () => api<ShareData>(`/api/public/share/${slug}`) })
  const phases = new Map<number, ShareFeature[]>()
  for (const f of data?.features ?? []) phases.set(f.phase, [...(phases.get(f.phase) ?? []), f])
  const ordered = [...phases.entries()].sort((a, b) => a[0] - b[0])
  const proof = data?.proof
  return (
    <>
      <header className="flex h-14 shrink-0 items-center justify-between border-b px-4">
        <Wordmark />
        <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">{m.share_readonly()}</span>
      </header>
      <main className="flex-1 overflow-y-auto">
        <div className="px-4 py-8 sm:px-6 sm:py-10">
          {error ? (
            <p className="text-center text-destructive">{m.err_not_found_title()}</p>
          ) : data ? (
            <div className="mx-auto grid w-full max-w-5xl gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
              <article className="min-w-0">
                <h1 className="mb-6 text-3xl font-bold sm:text-4xl">{data.title}</h1>
                <Markdown content={data.prd} />
              </article>
              <aside className="space-y-5 lg:sticky lg:top-0 lg:self-start">
                {proof && proof.tasks > 0 ? <ProofCard proof={proof} approvals={data.approvals ?? []} /> : null}
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{m.share_features_title()}</h2>
                {ordered.map(([phase, list]) => (
                  <section key={phase} className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground">{m.share_phase_title({ n: phase })}</span>
                      <span className="text-muted-foreground">{list.length}</span>
                    </div>
                    {list.map((f) => (
                      <div key={f.name} className="rounded-xl border bg-card p-3 text-sm">
                        <div className="flex items-start gap-2.5">
                          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground"><FeatureIcon icon={f.icon} /></span>
                          <div className="min-w-0">
                            <div className="font-medium leading-snug">{f.name}</div>
                            {f.summary ? <div className="mt-0.5 text-xs text-muted-foreground">{f.summary}</div> : null}
                          </div>
                        </div>
                        {f.evidence ? <EvidenceBar e={f.evidence} /> : null}
                      </div>
                    ))}
                  </section>
                ))}
              </aside>
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground">{m.common_loading()}</p>
          )}
        </div>
        <footer className="border-t bg-card/60">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm sm:flex-row sm:px-6">
            <span className="flex items-center gap-2 text-muted-foreground">{m.share_made_with()} <span className="wordmark text-foreground">by<span className="dot">·</span>step</span></span>
            <Link to="/" className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
              {m.share_footer_cta()} <ArrowRight className="size-4" />
            </Link>
          </div>
        </footer>
      </main>
    </>
  )
}
